import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LoginForm } from '../components/LoginForm';
import { RegisterWizard } from '../components/RegisterWizard';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_REG = {
  first_name: '', last_name: '', email: '', phone: '', password: '',
  ragione_sociale: '', indirizzo: '', cap: '', citta: '', paese: 'Italia',
  partita_iva: '', codice_fiscale: '', pec_sdi: '',
  billing_same_as_personal: true,
  billing_ragione_sociale: '', billing_indirizzo: '', billing_cap: '',
  billing_citta: '', billing_paese: '', billing_partita_iva: '',
  billing_codice_fiscale: '', billing_pec_sdi: '',
  accept_lpd: false, accept_disclaimer: false,
};

export default function LoginPage() {
  const { login, register, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regStep, setRegStep] = useState(1);
  const [reg, setReg] = useState(INITIAL_REG);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLpd, setShowLpd] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const updateReg = (field, value) => setReg(prev => ({ ...prev, [field]: value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!reg.accept_lpd || !reg.accept_disclaimer) {
      setError(t('mustAcceptTerms'));
      return;
    }
    setError('');
    setLoading(true);
    const result = await register(reg);
    if (!result.success) setError(result.error);
    else toast.success(t('registerCheckEmail'));
    setLoading(false);
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setRegStep(1);
    setError('');
  };

  const isLogin = mode === 'login';

  return (
    <div className="login-split">
      <div className="login-image-side"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1626187777040-ffb7cb2c5450?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3dvcmtpbmclMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzc0OTY5NzIzfDA&ixlib=rb-4.1.0&q=85')` }}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <h1 className="text-4xl font-extrabold mb-2">{t('systemName')}</h1>
          <p className="text-lg opacity-90">{t('loginDescription')}</p>
        </div>
      </div>

      <div className="login-form-side bg-slate-50">
        <div className="w-full max-w-lg animate-fade-in-up overflow-y-auto max-h-[90vh] px-2">
          <div className="card-floating">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                {isLogin ? <LogIn className="w-7 h-7 text-white" /> : <UserPlus className="w-7 h-7 text-white" />}
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900">
                {isLogin ? t('welcome') : t('registerTitle')}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {isLogin ? t('loginSubtitle') : `${t('step')} ${regStep}/3`}
              </p>
            </div>

            {error && (
              <div data-testid="auth-error" className="bg-rose-50 text-rose-600 px-4 py-3 rounded-xl text-sm font-medium mb-4">
                {error}
              </div>
            )}

            {isLogin ? (
              <LoginForm email={email} setEmail={setEmail} password={password} setPassword={setPassword}
                loading={loading} onSubmit={handleLogin} t={t} />
            ) : (
              <RegisterWizard reg={reg} updateReg={updateReg} step={regStep} setStep={setRegStep}
                setError={setError} loading={loading} onSubmit={handleRegister}
                onShowLpd={() => setShowLpd(true)} onShowDisclaimer={() => setShowDisclaimer(true)} t={t} />
            )}

            <div className="mt-5 text-center">
              <p className="text-slate-500 text-sm">
                {isLogin ? t('noAccount') : t('alreadyHaveAccount')}{' '}
                <button type="button" onClick={switchMode} data-testid="toggle-auth-mode"
                  className="text-blue-600 font-bold hover:underline">
                  {isLogin ? t('goToRegister') : t('goToLogin')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showLpd} onOpenChange={setShowLpd}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="lpd-desc">
          <DialogHeader>
            <DialogTitle>{t('readLpd') || 'Informativa Privacy'}</DialogTitle>
          </DialogHeader>
          <p id="lpd-desc" className="text-sm text-slate-600 mt-2">{t('lpdText')}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="disc-desc">
          <DialogHeader>
            <DialogTitle>{t('readDisclaimer') || 'Regolamento'}</DialogTitle>
          </DialogHeader>
          <p id="disc-desc" className="text-sm text-slate-600 mt-2">{t('disclaimerText')}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
